import { REFERRALS_ENABLED } from "@/lib/features";
import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { z } from "zod";
import { Check, Circle, Loader2, Mail, Info } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "@/hooks/use-toast";
import { consumePostAuthRedirect } from "@/lib/postAuthRedirect";
import { supabase } from "@/integrations/supabase/client";
import { lovable } from "@/integrations/lovable";
import { track } from "@/lib/posthog";
import { getReferralCode } from "@/lib/referrals";
import logo from "@/assets/huelo-logo.svg";

const emailSchema = z.string().trim().email().max(255);
const passwordSchema = z.string().min(6).max(72);

type Mode = "signup" | "login";

const AuthPage = () => {
  const navigate = useNavigate();
  const [mode, setMode] = useState<Mode>("signup");

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      if (data.session) navigate(consumePostAuthRedirect(), { replace: true });
    });
    // Popup-based sign-in (e.g. Google on preview links) sets the session in
    // place without a page redirect — move on as soon as it lands.
    const { data: sub } = supabase.auth.onAuthStateChange((event) => {
      if (event === "SIGNED_IN") setTimeout(() => { if (window.location.pathname === "/") navigate("/auth/callback", { replace: true }); }, 0);
    });
    return () => sub.subscription.unsubscribe();
  }, [navigate]);

  const [signupFirstName, setSignupFirstName] = useState("");
  const [signupLastName, setSignupLastName] = useState("");
  const [signupEmail, setSignupEmail] = useState("");
  const [signupPassword, setSignupPassword] = useState("");
  const [signupLoading, setSignupLoading] = useState(false);
  const [signupSuccessEmail, setSignupSuccessEmail] = useState<string | null>(null);
  const [alreadyRegisteredEmail, setAlreadyRegisteredEmail] = useState<string | null>(null);
  const [acceptedTos, setAcceptedTos] = useState(false);
  const [tosHintVisible, setTosHintVisible] = useState(false);
  const tosRef = useRef<HTMLButtonElement | null>(null);
  const [marketingOptIn, setMarketingOptIn] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [resendLoading, setResendLoading] = useState(false);

  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [loginLoading, setLoginLoading] = useState(false);

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const t = setTimeout(() => setResendCooldown((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [resendCooldown]);

  const showError = (message: string) =>
    toast({ title: "Something went wrong", description: message, variant: "destructive" });

  const switchToLogin = (email?: string) => {
    if (email) setLoginEmail(email);
    setAlreadyRegisteredEmail(null);
    setSignupSuccessEmail(null);
    setMode("login");
  };

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    setAlreadyRegisteredEmail(null);
    const firstName = signupFirstName.trim();
    const lastName = signupLastName.trim();
    if (!firstName) return showError("Please enter your first name.");
    if (!acceptedTos) {
      setTosHintVisible(true);
      tosRef.current?.focus();
      tosRef.current?.scrollIntoView({ block: "center", behavior: "smooth" });
      return;
    }
    if (!emailSchema.safeParse(signupEmail).success) return showError("Enter a valid email.");
    if (!passwordSchema.safeParse(signupPassword).success) return showError("Password must be at least 6 characters.");

    setSignupLoading(true);
    try {
      const email = signupEmail.trim().toLowerCase();
      const fullName = [firstName, lastName].filter(Boolean).join(" ");
      const { data, error } = await supabase.auth.signUp({
        email,
        password: signupPassword,
        options: {
          emailRedirectTo: window.location.origin + "/dashboard",
          data: {
            marketing_opt_in: marketingOptIn,
            first_name: firstName,
            last_name: lastName || null,
            full_name: fullName,
          },
        },
      });
      if (error) {
        const msg = error.message || "";
        if (/already registered|already exists/i.test(msg)) {
          setAlreadyRegisteredEmail(email);
        } else if (/rate|too many/i.test(msg)) {
          showError("Too many attempts. Please wait a moment and try again.");
        } else {
          showError(msg);
        }
        return;
      }
      // Supabase returns an empty identities array when the email is already registered.
      const identities = data.user?.identities ?? [];
      if (data.user && identities.length === 0) {
        setAlreadyRegisteredEmail(email);
        return;
      }

      // Persist marketing preference + names to profile as soon as we can
      // (either now if a session exists, or after email confirmation via AuthCallback).
      if (data.session && data.user) {
        await supabase
          .from("profiles")
          .update({
            marketing_opt_in: marketingOptIn,
            marketing_opt_in_at: marketingOptIn ? new Date().toISOString() : null,
            first_name: firstName,
            last_name: lastName || null,
          })
          .eq("user_id", data.user.id);
      } else {
        // Stash for AuthCallback / next sign-in to persist.
        try {
          localStorage.setItem("huelo:pending_marketing_opt_in", marketingOptIn ? "1" : "0");
          localStorage.setItem("huelo:pending_first_name", firstName);
          localStorage.setItem("huelo:pending_last_name", lastName);
        } catch { /* ignore */ }
      }

      // Auto-confirmed (e.g. @huelo.co) — session present, go to app.
      // The PostHogBootstrap onAuthStateChange listener will fire user_signed_up.
      if (data.session) {
        navigate(consumePostAuthRedirect(), { replace: true });
        return;
      }

      // New signup awaiting email confirmation. Track here since the user may
      // confirm the email in a different browser; PostHogBootstrap dedupe
      // ensures we don't double-count if they confirm in this browser.
      track("user_signed_up", { source: "email", marketing_opt_in: marketingOptIn });
      setSignupSuccessEmail(email);
      setResendCooldown(30);
    } finally {
      setSignupLoading(false);
    }
  };

  const handleResend = async () => {
    if (!signupSuccessEmail || resendCooldown > 0 || resendLoading) return;
    setResendLoading(true);
    try {
      const { error } = await supabase.auth.resend({
        type: "signup",
        email: signupSuccessEmail,
        options: { emailRedirectTo: window.location.origin + "/dashboard" },
      });
      if (error) {
        showError(error.message);
        return;
      }
      toast({ title: "Verification email sent", description: `We sent another link to ${signupSuccessEmail}.` });
      setResendCooldown(30);
    } finally {
      setResendLoading(false);
    }
  };

  const handleUseDifferentEmail = () => {
    setSignupSuccessEmail(null);
    setSignupPassword("");
  };

  // Several auth calls racing on page load can make the browser's auth lock
  // abort the sign-in step ("Lock broken by another request with the 'steal'
  // option") even though Google approved the user. Re-check the session
  // before showing an error.
  const handleOAuthError = async (err: unknown) => {
    const msg = String((err as Error)?.message ?? err);
    if (/lock/i.test(msg)) {
      for (let i = 0; i < 3; i++) {
        await new Promise((r) => setTimeout(r, 400 * (i + 1)));
        const { data } = await supabase.auth.getSession();
        if (data.session) {
          navigate("/auth/callback", { replace: true });
          return;
        }
      }
      return showError("Sign-in was interrupted. Please try again.");
    }
    showError(msg);
  };

  const handleGoogleSignup = async () => {
    // No ToS checkbox gate here: the inline legal notice under the Google
    // button covers acceptance, and marketing consent is collected after
    // sign-in (Google's consent screen can't host our own checkboxes).
    // Do NOT fire user_signed_up here — the Google button signs in returning
    // users too. PostHogBootstrap fires user_signed_up exactly once per new
    // account inside onAuthStateChange.
    const result = await lovable.auth.signInWithOAuth("google", {
      redirect_uri: window.location.origin + "/auth/callback",
    });
    if (result.error) await handleOAuthError(result.error);
    else if (!result.redirected) navigate("/auth/callback", { replace: true });
  };


  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!emailSchema.safeParse(loginEmail).success) return showError("Enter a valid email.");
    if (!passwordSchema.safeParse(loginPassword).success) return showError("Password must be at least 6 characters.");
    setLoginLoading(true);
    const { error } = await supabase.auth.signInWithPassword({
      email: loginEmail.trim().toLowerCase(),
      password: loginPassword,
    });
    setLoginLoading(false);
    if (error) {
      const msg = error.message || "";
      if (/email not confirmed/i.test(msg)) {
        return showError("Please verify your email first. Check your inbox for the confirmation link.");
      }
      return showError(msg);
    }
    navigate(consumePostAuthRedirect(), { replace: true });
  };

  const handleGoogleLogin = async () => {
    const result = await lovable.auth.signInWithOAuth("google", {
      redirect_uri: window.location.origin + "/auth/callback",
    });
    if (result.error) await handleOAuthError(result.error);
    else if (!result.redirected) navigate("/auth/callback", { replace: true });
  };

  const handleAppleSignIn = async () => {
    // Same rationale as Google: no ToS checkbox gate, marketing consent is
    // collected after sign-in. Used for both sign-up and log-in.
    const result = await lovable.auth.signInWithOAuth("apple", {
      redirect_uri: window.location.origin + "/auth/callback",
    });
    if (result.error) await handleOAuthError(result.error);
    else if (!result.redirected) navigate("/auth/callback", { replace: true });
  };


  const passwordChecks = [
    { label: "At least 6 characters", ok: signupPassword.length >= 6 },
    { label: "Contains a letter", ok: /[A-Za-z]/.test(signupPassword) },
    { label: "Contains a number", ok: /\d/.test(signupPassword) },
  ];

  return (
    <div className="min-h-[100dvh] bg-background flex flex-col items-center justify-center px-6 py-10">
      <div className="w-full max-w-md">
        <div className="flex flex-col items-center mb-8">
          <a href="https://huelo.co" aria-label="Go to huelo.co" rel="noopener noreferrer">
            <img src={logo} alt="Huelo" className="h-10 w-auto mb-3" />
          </a>
          <h1 className="font-display text-2xl md:text-4xl tracking-tight text-foreground">Huelo</h1>
        </div>

        {REFERRALS_ENABLED && getReferralCode() && (
          <div className="mb-4 rounded-lg bg-muted px-4 py-3 text-center">
            <p className="text-sm font-semibold text-foreground">Your friend invited you to Huelo</p>
            <p className="text-xs text-muted-foreground mt-0.5">Create an account to redesign your room with AI.</p>
          </div>
        )}
        <div className="rounded-lg border border-border bg-card p-6 shadow-sm">
          {mode === "signup" && signupSuccessEmail && (
            <div aria-live="polite">
              <div className="flex flex-col items-center text-center">
                <div className="h-12 w-12 rounded-full bg-muted flex items-center justify-center mb-4">
                  <Mail className="h-6 w-6 text-foreground" />
                </div>
                <h2 className="font-display text-xl text-foreground">Check your email</h2>
                <p className="text-sm text-muted-foreground mt-2">
                  We sent a verification link to{" "}
                  <span className="font-medium text-foreground">{signupSuccessEmail}</span>. Click the link to
                  activate your account, then come back here to log in.
                </p>
              </div>

              <div className="mt-6 space-y-2">
                <Button
                  type="button"
                  className="w-full rounded-full"
                  onClick={() => switchToLogin(signupSuccessEmail)}
                >
                  Go to log in
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  className="w-full rounded-full"
                  onClick={handleResend}
                  disabled={resendCooldown > 0 || resendLoading}
                >
                  {resendLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  {resendCooldown > 0 ? `Resend email in ${resendCooldown}s` : "Resend email"}
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  className="w-full rounded-full"
                  onClick={handleUseDifferentEmail}
                >
                  Use a different email
                </Button>
              </div>

              <p className="text-xs text-muted-foreground text-center mt-4">
                Didn't get it? Check your spam folder.
              </p>
            </div>
          )}

          {mode === "signup" && !signupSuccessEmail && (
            <>
              <div className="mb-5">
                <h2 className="font-display text-xl text-foreground">Create your account</h2>
                <p className="text-sm text-muted-foreground mt-1">
                  4 free room designs every month. No credit card required.
                </p>
              </div>

              <Button
                type="button"
                variant="outline"
                className="w-full rounded-full"
                onClick={handleGoogleSignup}
                disabled={signupLoading}
              >
                Continue with Google
              </Button>
              <Button
                type="button"
                variant="outline"
                className="w-full rounded-full mt-2"
                onClick={handleAppleSignIn}
                disabled={signupLoading}
              >
                Continue with Apple
              </Button>
              <p className="text-xs text-muted-foreground text-center mt-2 leading-relaxed">
                By continuing you agree to Huelo's{" "}
                <Link to="/privacy" className="underline underline-offset-2 hover:text-foreground">
                  Privacy Policy
                </Link>{" "}
                and Terms.
              </p>




              <div className="my-6 flex items-center gap-3">
                <Separator className="flex-1" />
                <span className="text-xs text-muted-foreground">or</span>
                <Separator className="flex-1" />
              </div>

              {alreadyRegisteredEmail && (
                <div
                  role="status"
                  aria-live="polite"
                  className="mb-4 flex items-start gap-3 rounded-lg border border-border bg-muted/50 p-3 text-sm"
                >
                  <Info className="h-4 w-4 mt-0.5 text-muted-foreground shrink-0" />
                  <div className="flex-1">
                    <p className="text-foreground">
                      An account with <span className="font-medium">{alreadyRegisteredEmail}</span> already exists.
                    </p>
                    <button
                      type="button"
                      onClick={() => switchToLogin(alreadyRegisteredEmail)}
                      className="mt-1 text-foreground underline underline-offset-4 hover:opacity-80"
                    >
                      Log in instead
                    </button>
                  </div>
                </div>
              )}

              <form onSubmit={handleSignup} className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 sm:items-end gap-4 sm:gap-3">
                  <div className="space-y-2">
                    <Label htmlFor="signup-first-name">First name</Label>
                    <Input
                      id="signup-first-name"
                      type="text"
                      autoComplete="given-name"
                      value={signupFirstName}
                      onChange={(e) => setSignupFirstName(e.target.value)}
                      disabled={signupLoading}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="signup-last-name" className="whitespace-nowrap">
                      Last name <span className="text-muted-foreground font-normal">(optional)</span>
                    </Label>
                    <Input
                      id="signup-last-name"
                      type="text"
                      autoComplete="family-name"
                      value={signupLastName}
                      onChange={(e) => setSignupLastName(e.target.value)}
                      disabled={signupLoading}
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="signup-email">Email</Label>
                  <Input
                    id="signup-email"
                    type="email"
                    autoComplete="email"
                    value={signupEmail}
                    onChange={(e) => setSignupEmail(e.target.value)}
                    disabled={signupLoading}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="signup-password">Password</Label>
                  <Input
                    id="signup-password"
                    type="password"
                    autoComplete="new-password"
                    value={signupPassword}
                    onChange={(e) => setSignupPassword(e.target.value)}
                    minLength={6}
                    disabled={signupLoading}
                    required
                  />
                  <ul className="space-y-1.5 pt-1">
                    {passwordChecks.map((c) => (
                      <li
                        key={c.label}
                        className={`flex items-center gap-2 text-xs transition-colors ${
                          c.ok ? "text-success" : "text-muted-foreground"
                        }`}
                      >
                        {c.ok ? (
                          <Check className="h-3.5 w-3.5" />
                        ) : (
                          <Circle className="h-3.5 w-3.5" />
                        )}
                        {c.label}
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="space-y-3 pt-1">
                  <div
                    className={`-mx-2 px-2 py-2 rounded-lg transition-colors ${
                      tosHintVisible ? "bg-muted/60 border border-border" : "border border-transparent"
                    }`}
                  >
                    <label htmlFor="signup-tos" className="flex items-start gap-3 cursor-pointer">
                      <Checkbox
                        id="signup-tos"
                        ref={tosRef}
                        checked={acceptedTos}
                        onCheckedChange={(v) => {
                          const next = v === true;
                          setAcceptedTos(next);
                          if (next) setTosHintVisible(false);
                        }}
                        disabled={signupLoading}
                        className="mt-0.5"
                        aria-describedby={tosHintVisible ? "signup-tos-hint" : undefined}
                        required
                      />
                      <span className="text-xs text-muted-foreground leading-relaxed">
                        I agree to Huelo's{" "}
                        <Link to="/privacy" className="underline underline-offset-2 hover:text-foreground">
                          Privacy Policy
                        </Link>
                        {" "}and Terms. <span className="text-foreground">(required)</span>
                      </span>
                    </label>
                    {tosHintVisible && (
                      <p
                        id="signup-tos-hint"
                        className="mt-1.5 pl-7 text-xs text-foreground animate-in fade-in-0 slide-in-from-top-1"
                      >
                        Tick this to continue — we need your agreement to create the account.
                      </p>
                    )}
                  </div>
                  <label htmlFor="signup-marketing" className="flex items-start gap-3 cursor-pointer">
                    <Checkbox
                      id="signup-marketing"
                      checked={marketingOptIn}
                      onCheckedChange={(v) => setMarketingOptIn(v === true)}
                      disabled={signupLoading}
                      className="mt-0.5"
                    />
                    <span className="text-xs text-muted-foreground leading-relaxed">
                      Send me product news, special offers, and promotional emails. I can unsubscribe at any time.
                    </span>
                  </label>
                </div>
                <Button type="submit" className="w-full rounded-full" disabled={signupLoading}>

                  {signupLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Create account
                </Button>
                <p className="text-xs text-muted-foreground text-center leading-relaxed">
                  We'll send a verification link to your email to activate your account.
                </p>
              </form>

              <p className="text-center text-sm text-muted-foreground mt-6">
                Already have an account?{" "}
                <button
                  type="button"
                  onClick={() => setMode("login")}
                  className="text-foreground underline underline-offset-4 hover:opacity-80"
                >
                  Log in
                </button>
              </p>
            </>
          )}

          {mode === "login" && (
            <>
              <div className="mb-5">
                <h2 className="font-display text-xl text-foreground">Welcome back</h2>
                <p className="text-sm text-muted-foreground mt-1">Log in to your account.</p>
              </div>
              <form onSubmit={handleLogin} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="login-email">Email</Label>
                  <Input
                    id="login-email"
                    type="email"
                    autoComplete="email"
                    value={loginEmail}
                    onChange={(e) => setLoginEmail(e.target.value)}
                    disabled={loginLoading}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="login-password">Password</Label>
                  <Input
                    id="login-password"
                    type="password"
                    autoComplete="current-password"
                    value={loginPassword}
                    onChange={(e) => setLoginPassword(e.target.value)}
                    disabled={loginLoading}
                    required
                  />
                </div>
                <Button type="submit" className="w-full rounded-full" disabled={loginLoading}>
                  {loginLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Log in
                </Button>
              </form>

              <div className="my-6 flex items-center gap-3">
                <Separator className="flex-1" />
                <span className="text-xs text-muted-foreground">or</span>
                <Separator className="flex-1" />
              </div>

              <Button type="button" variant="outline" className="w-full rounded-full" onClick={handleGoogleLogin}>
                Continue with Google
              </Button>
              <Button type="button" variant="outline" className="w-full rounded-full mt-2" onClick={handleAppleSignIn}>
                Continue with Apple
              </Button>


              <p className="text-center text-sm text-muted-foreground mt-6">
                New here?{" "}
                <button
                  type="button"
                  onClick={() => setMode("signup")}
                  className="text-foreground underline underline-offset-4 hover:opacity-80"
                >
                  Sign up
                </button>
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AuthPage;
